'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { MessageCircle, Share2, ThumbsUp, Volume2, VolumeX } from 'lucide-react'
import ShortVideoPlayer from '@/components/shorts/ShortVideoPlayer'
import ShortsAuthorRow from '@/components/shorts/ShortsAuthorRow'
import ShortsCommentsPanel from '@/components/shorts/ShortsCommentsPanel'
import ShortsDescriptionPanel from '@/components/shorts/ShortsDescriptionPanel'
import { getApiUrl } from '@/lib/config'
import type { ShortsAudioControl } from '@/lib/shorts-audio-control'
import { primeAllShortsAudio } from '@/lib/shorts-audio-session'
import type { ShortFeedItem } from '@/lib/news-types'
import { fetchNewsLikeStatus, toggleNewsLike } from '@/lib/shorts-like-api'
import { formatShortCount, getShortsCanonicalUrlForItem } from '@/lib/shorts-slug'
import styles from '@/components/shorts/shorts.module.css'

interface ShortVideoSlideProps {
  item: ShortFeedItem
  isActive: boolean
  shouldPreload: boolean
  shouldWarm: boolean
  muted: boolean
  volume: number
  audioUnlocked: boolean
  onAudioChange: (next: { muted?: boolean; volume?: number }) => void
  useSharedYoutube?: boolean
  sharedPlaying?: boolean
  onSharedPlayingChange?: (playing: boolean) => void
  slideRef?: (el: HTMLElement | null) => void
}

export default function ShortVideoSlide({
  item,
  isActive,
  shouldPreload,
  shouldWarm,
  muted,
  volume,
  audioUnlocked,
  onAudioChange,
  useSharedYoutube = false,
  sharedPlaying = true,
  onSharedPlayingChange,
  slideRef,
}: ShortVideoSlideProps) {
  const [playing, setPlaying] = useState(true)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [descriptionOpen, setDescriptionOpen] = useState(false)
  const [commentCount, setCommentCount] = useState(item.commentCount ?? 0)
  const [liked, setLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(item.likeCount ?? 0)
  const [likeBusy, setLikeBusy] = useState(false)
  const audioControlRef = useRef<ShortsAudioControl | null>(null)
  const isYoutubeShort = item.source === 'youtube'
  const usesSharedYoutube = useSharedYoutube && isYoutubeShort
  const commentsEnabled = !isYoutubeShort && Number(item.comments ?? 1) !== 0
  const likesEnabled = !isYoutubeShort
  // Autoplay is policy-muted until the user unlocks audio; keep the icon honest.
  const effectivelyMuted = muted || !audioUnlocked
  const effectivePlaying = usesSharedYoutube ? sharedPlaying : playing

  const applyAudioChange = useCallback(
    (next: { muted: boolean; volume: number }) => {
      // Unmute every mounted Shorts player inside this gesture so neighbors
      // keep audio after swipe without needing another unMute() call.
      primeAllShortsAudio(next.muted, next.volume)
      audioControlRef.current?.applyUserAudio(next.muted, next.volume)
      onAudioChange(next)
    },
    [onAudioChange],
  )

  useEffect(() => {
    setCommentCount(item.commentCount ?? 0)
  }, [item.id, item.commentCount])

  useEffect(() => {
    setLikeCount(item.likeCount ?? 0)
    if (!isActive || !likesEnabled) return
    let cancelled = false
    void fetchNewsLikeStatus(item.id)
      .then((status) => {
        if (!cancelled) {
          setLiked(status.liked)
          setLikeCount(status.likeCount)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isActive, item.id, item.likeCount, likesEnabled])

  useEffect(() => {
    // Slayt pasifken player zaten `isActive` üzerinden durduruluyor/ısıtılıyor.
    // Burada `playing=false` saklamak, sonraki aktivasyonda bir render boyunca
    // hazır videoya pause gönderip hemen ardından yeniden play verilmesine ve
    // YouTube'un merkez play/pause bezeline yol açıyordu. Bir sonraki geçişin
    // oynatma niyetini pasifken hazır tut; kullanıcı durdurması yalnızca aktif
    // slaytta geçerli kalsın.
    setPlaying(true)
    if (!isActive) {
      setCommentsOpen(false)
      setDescriptionOpen(false)
    }
  }, [isActive, item.id])

  const handleLike = useCallback(async () => {
    if (likeBusy || !likesEnabled) return
    const prevLiked = liked
    const prevCount = likeCount
    setLikeBusy(true)
    setLiked(!prevLiked)
    setLikeCount(prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1)
    try {
      const result = await toggleNewsLike(item.id)
      setLiked(result.liked)
      setLikeCount(result.likeCount)
    } catch {
      setLiked(prevLiked)
      setLikeCount(prevCount)
    } finally {
      setLikeBusy(false)
    }
  }, [item.id, likeBusy, liked, likeCount, likesEnabled])

  const handleShare = useCallback(async () => {
    const shareUrl = getShortsCanonicalUrlForItem(item)
    if (item.source !== 'youtube') {
      void fetch(`${getApiUrl()}/news/${item.id}/share`, {
        method: 'GET',
        keepalive: true,
      }).catch(() => {})
    }
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: item.title, url: shareUrl })
        return
      }
      await navigator.clipboard.writeText(shareUrl)
    } catch {
      /* ignore */
    }
  }, [item])

  const openDescription = useCallback(() => {
    setCommentsOpen(false)
    setDescriptionOpen(true)
  }, [])

  return (
    <section
      ref={slideRef}
      className={`shorts-slide ${styles.slide}${usesSharedYoutube ? '' : ` ${styles.slideOpaque}`}`}
      aria-label={item.title}
    >
      <div
        className={styles.frame}
        style={usesSharedYoutube ? { background: 'transparent' } : undefined}
      >
        <div
          className={styles.mediaLayer}
          style={usesSharedYoutube ? { background: 'transparent' } : undefined}
        >
          {usesSharedYoutube ? (
            isActive ? (
              <div
                className={styles.playbackSurface}
                role="button"
                tabIndex={0}
                aria-label={effectivePlaying ? 'Videoyu durdur' : 'Videoyu oynat'}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  onSharedPlayingChange?.(!effectivePlaying)
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  event.stopPropagation()
                  onSharedPlayingChange?.(!effectivePlaying)
                }}
              />
            ) : null
          ) : (
            <ShortVideoPlayer
              video={item.video}
              youtube={item.youtube}
              dailymotion={item.dailymotion}
              vimeo={item.vimeo}
              x={item.x}
              facebook={item.facebook}
              playerType={item.playerType}
              isActive={isActive}
              shouldPreload={shouldPreload}
              shouldWarm={shouldWarm}
              playing={playing}
              muted={muted}
              volume={volume}
              audioUnlocked={audioUnlocked}
              audioControlRef={audioControlRef}
              useSharedYoutube={useSharedYoutube}
              onPlayingChange={setPlaying}
              onAudioChange={onAudioChange}
            />
          )}
        </div>

        {isActive ? (
          <div className={styles.audioControls} data-shorts-control>
            <label className={styles.audioRange}>
              <span className="sr-only">Ses düzeyi</span>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={effectivelyMuted ? 0 : volume}
                aria-label="Ses düzeyi"
                onPointerDown={(event) => event.stopPropagation()}
                onPointerUp={(event) => event.stopPropagation()}
                onChange={(event) => {
                  const nextVolume = Number(event.target.value)
                  applyAudioChange({
                    muted: nextVolume <= 0,
                    volume: nextVolume,
                  })
                }}
              />
            </label>
            <button
              type="button"
              data-shorts-control
              className={styles.audioButton}
              aria-label={effectivelyMuted ? 'Sesi aç' : 'Sesi kapat'}
              aria-pressed={effectivelyMuted}
              onPointerDown={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation()
                applyAudioChange({
                  muted: !effectivelyMuted,
                  volume: volume > 0 ? volume : 50,
                })
              }}
            >
              {effectivelyMuted ? (
                <VolumeX className="h-5 w-5" />
              ) : (
                <Volume2 className="h-5 w-5" />
              )}
            </button>
          </div>
        ) : null}

        <div className={styles.actions} data-shorts-control>
          {likesEnabled ? (
            <>
              <button
                type="button"
                data-shorts-control
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleLike()
                }}
                disabled={likeBusy}
                className={`${styles.actionBtn} ${liked ? styles.actionBtnActive : ''}`}
                aria-label="Beğen"
                aria-pressed={liked}
              >
                <ThumbsUp className={`h-6 w-6 ${liked ? 'fill-white' : ''}`} />
              </button>
              <span className={styles.actionCount}>{formatShortCount(likeCount)}</span>
            </>
          ) : null}

          {commentsEnabled ? (
            <>
              <button
                type="button"
                data-shorts-control
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation()
                  setDescriptionOpen(false)
                  setCommentsOpen(true)
                }}
                className={styles.actionBtn}
                aria-label="Yorumlar"
              >
                <MessageCircle className="h-6 w-6" />
              </button>
              <span className={styles.actionCount}>{formatShortCount(commentCount)}</span>
            </>
          ) : null}

          <button
            type="button"
            data-shorts-control
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              void handleShare()
            }}
            className={styles.actionBtn}
            aria-label="Paylaş"
          >
            <Share2 className="h-6 w-6" />
          </button>
          <span className={styles.actionCount}>{formatShortCount(item.shareCount ?? 0)}</span>
        </div>

        <div className={`shorts-overlay ${styles.bottom} ${styles.bottomWithPlyr}`}>
          <div className={styles.meta}>
            {item.editor ? (
              <ShortsAuthorRow
                editor={item.editor}
                youtubeChannel={item.youtubeChannel}
                onOpenDescription={openDescription}
              />
            ) : null}

            <button
              type="button"
              data-shorts-control
              className={`${styles.title} ${styles.titleClamp} ${styles.titleButton}`}
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                openDescription()
              }}
            >
              {item.title}
            </button>
          </div>
        </div>
      </div>

      <ShortsDescriptionPanel
        item={item}
        open={descriptionOpen && isActive}
        onClose={() => setDescriptionOpen(false)}
      />

      <ShortsCommentsPanel
        newsId={item.id}
        title={item.title}
        open={commentsOpen && isActive}
        onClose={() => setCommentsOpen(false)}
        onCountChange={setCommentCount}
      />
    </section>
  )
}
